import { useEffect, useMemo, useRef, useState } from 'react';

import { Download } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import Loader from './Loader';
import Modal from './Modal';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface FileViewerProps {
  fileName: string;
  sasUrl: string;
  onClose: () => void;
}

const getFileType = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (!ext) {
    return 'unknown';
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    return 'image';
  }
  if (['pdf'].includes(ext)) {
    return 'pdf';
  }
  if (['mp4', 'webm', 'ogg'].includes(ext)) {
    return 'video';
  }
  if (['mp3', 'wav'].includes(ext)) {
    return 'audio';
  }
  if (['txt', 'csv', 'log'].includes(ext)) {
    return 'text';
  }

  return 'unknown';
};

export const FileViewer: React.FC<FileViewerProps> = ({
  fileName,
  sasUrl,
  onClose,
}) => {
  const type = useMemo(() => getFileType(fileName), [fileName]);
  const [numPages, setNumPages] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const resize = (): void => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    resize(); // initial

    const observer = new ResizeObserver(resize);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return (): void => observer.disconnect();
  }, []);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={fileName}
      actions={
        <a
          href={sasUrl}
          download
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center h-10 gap-2 px-3 text-sm cursor-pointer
          font-medium tracking-wide transition duration-300 rounded-full justify-self-center whitespace-nowrap
          text-secondary hover:bg-orange-200 hover:text-secondary focus:bg-orange-200 focus:text-secondary focus-visible:outline-none"
          aria-label="Download file"
        >
          <Download className="w-5 h-5" />
        </a>
      }
    >
      <div ref={containerRef} className="relative space-y-4 min-h-[200px]">
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10">
            <Loader />
            <p className="mt-2 text-sm text-gray-500">Loading file...</p>
          </div>
        )}

        {type === 'image' && (
          <img
            src={sasUrl}
            alt={fileName}
            onLoad={() => setLoading(false)}
            className="max-h-full max-w-full object-contain mx-auto"
          />
        )}

        {type === 'pdf' && (
          <div ref={containerRef} className="w-full overflow-hidden">
            <Document
              file={sasUrl}
              onLoadSuccess={({ numPages }) => {
                setNumPages(numPages);
                setLoading(false);
              }}
              onLoadError={(err) => console.error('PDF load error:', err)}
              onSourceError={(err) => console.error('PDF source error:', err)}
            >
              {Array.from({ length: numPages }, (_, index) => (
                <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  width={containerWidth}
                />
              ))}
            </Document>
          </div>
        )}

        {type === 'video' && (
          <video
            controls
            className="max-w-full max-h-full mx-auto"
            onLoadedData={() => setLoading(false)}
          >
            <source src={sasUrl} />
            Your browser does not support the video tag.
          </video>
        )}

        {type === 'audio' && (
          <audio
            controls
            className="w-full"
            onLoadedData={() => setLoading(false)}
          >
            <source src={sasUrl} />
            Your browser does not support the audio element.
          </audio>
        )}

        {type === 'text' && (
          <iframe
            src={sasUrl}
            title={fileName}
            onLoad={() => setLoading(false)}
            className="w-full h-full border"
          />
        )}

        {type === 'unknown' && (
          <div className="text-center text-gray-500">
            Preview not available. Use the download link above.
          </div>
        )}
      </div>
    </Modal>
  );
};
